import { Fragment, useEffect, useState } from "react";
import type { CreditActivity, CreditState } from "api";
import { creditsApi } from "api";
import { formatCredits } from "components/Billing/credits.model";
import type { LoadStatus } from "hooks/useProfile";
import { formatMonth } from "utils/date.utils";
import { isForbidden, isUnauthenticated } from "utils/error.utils";
import type { GotoTarget } from "./dashboard.model";
import { DashboardCard } from "./DashboardCard";

type CreditsUsageCardProps = {
  credits: CreditState | null;
  onGoto: (target: GotoTarget) => void;
  onSessionLost: () => void;
};

/** Products the card lists before "View activity" takes over. */
const TOP = 5;

const BILLING: GotoTarget = { pane: "billing" };

/**
 * `[data-dq]`, "QCredits usage": where this month's credits went, top products
 * first, each bar its share of the month. Plan & Billing has the full picture.
 */
export const CreditsUsageCard = ({ credits, onGoto, onSessionLost }: CreditsUsageCardProps) => {
  const [activity, setActivity] = useState<CreditActivity | null>(null);
  const [status, setStatus] = useState<LoadStatus>("loading");

  // The dashboard remounts each time it is opened, so this reads the month afresh.
  useEffect(() => {
    let cancelled = false;

    creditsApi
      .activity()
      .then(({ data }) => {
        if (cancelled) return;
        setActivity(data);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthenticated(error)) {
          onSessionLost();
          return;
        }
        setStatus(isForbidden(error) ? "unverified" : "failed");
      });

    return () => {
      cancelled = true;
    };
  }, [onSessionLost]);

  const rows = (activity?.products ?? []).slice(0, TOP);
  const free = credits?.balance.free ?? 0;

  return (
    <DashboardCard
      title="QCredits usage"
      sub={rows.length === 0 ? "No QCredits spent yet" : formatMonth(activity?.month)}
      status={status}
      failure="Could not load your QCredits usage. Please refresh the page."
    >
      {rows.length === 0 ? (
        <>
          <p className="pc-note">
            You have {formatCredits(free)} free this month. Use them on any Qubic on the platform.
          </p>
          <div className="dsh__plan-a">
            <button type="button" className="pcbtn" onClick={() => onGoto(BILLING)}>
              Buy QCredits
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="dsh__meter">
            {rows.map((row) => (
              <Fragment key={row.product ?? row.name}>
                <div className="dsh__meter-r">
                  <span>{row.name}</span>
                  <b>{formatCredits(row.credits)}</b>
                </div>
                <div className="dsh__bar">
                  <i style={{ width: `${Math.max(3, (row.credits / Math.max(1, activity?.spent ?? 1)) * 100)}%` }} />
                </div>
              </Fragment>
            ))}
          </div>
          <div className="dsh__plan-a">
            <button type="button" className="pcbtn" onClick={() => onGoto(BILLING)}>
              View activity
            </button>
          </div>
        </>
      )}
    </DashboardCard>
  );
};
